import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Modal from '@/components/atoms/Modal';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Form/Input';
import { getUserToken } from '@/redux/user';
import { computeText } from '@/locales/utils';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { SvyIcon } from '@/components/savory/SvyIcon';
import {
    useCreatePromotionalBannerMutation,
    useUpdatePromotionalBannerMutation,
    useTogglePromotionalBannerMutation,
} from '@/services/innovorder/promotionalBanner/promotionalBanner.endpoints';
import type { PromotionalBanner, LinkType } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';
import type { PickupScheduleSlot } from '@/services/innovorder/restaurant/restaurant.type';
import { useGetMenusQuery } from '@/services/innovorder/menu/menu.endpoints';
import { ChannelId } from '@/services/innovorder/channel/channel.type';
import { useGetCategoriesQuery } from '@/services/innovorder/categories/categories.endpoints';
import { useGetProductsQuery } from '@/services/innovorder/products/products.endpoints';

import { BannerImageSection } from '@/pages/PromotionalBanners/components/BannerImageSection/BannerImageSection';
import { ConfirmReloadModal } from '@/pages/PromotionalBanners/components/ConfirmReloadModal/ConfirmReloadModal';
import { isInServiceHours } from '@/pages/PromotionalBanners/utils/isInServiceHours';
import {
    HelperText,
    SectionLabel,
    RadioCardGroup,
    RadioCard,
    RadioCardContent,
    RadioCardTitle,
    RadioCardDescription,
    RadioDot,
    ChannelCheck,
    DisabledInfo,
    SearchInput,
    SelectedItem,
    ProductList,
    ProductItem,
    ProductThumb,
    ProductThumbPlaceholder,
    ProductName,
    ProductMeta,
    ProductMetaBold,
} from '@/pages/PromotionalBanners/BannerModal.style';

type FormValues = {
    name: string;
    imageId: number | null;
    linkType: LinkType;
    linkTargetId: number | null;
};

type Props = {
    isOpen: boolean;
    mode: 'create' | 'edit' | 'view';
    banner: PromotionalBanner | null;
    restaurantId: number;
    schedules: PickupScheduleSlot[];
    onHide: () => void;
};

export const PromotionalBannerModal: React.FC<Props> = ({ isOpen, mode, banner, restaurantId, schedules, onHide }) => {
    const isEdit = mode === 'edit';
    const isView = mode === 'view';
    const isActiveBanner = isEdit && banner?.isActive;
    const token = useSelector(getUserToken) as string;
    const intl = useIntl();

    const [createBanner] = useCreatePromotionalBannerMutation();
    const [updateBanner] = useUpdatePromotionalBannerMutation();
    const [toggleBanner] = useTogglePromotionalBannerMutation();

    const [showConfirmReload, setShowConfirmReload] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [isTargetListOpen, setIsTargetListOpen] = useState(true);

    const { data: menus = [] } = useGetMenusQuery({ restaurantId });
    const kioskMenuId = useMemo(() => {
        const kioskMenu = menus.find((m) => m.isActive && m.channels.some((c) => c.channelId === ChannelId.KIOSK));
        return kioskMenu?.menuId;
    }, [menus]);

    const { data: categories = [] } = useGetCategoriesQuery({ menuId: kioskMenuId! }, { skip: !kioskMenuId });
    const { data: products = [] } = useGetProductsQuery({ menuId: kioskMenuId! }, { skip: !kioskMenuId });

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { isDirty, isValid },
    } = useForm<FormValues>({
        defaultValues: {
            name: '',
            imageId: null,
            linkType: 'none',
            linkTargetId: null,
        },
    });

    const [currentLinkType, setCurrentLinkType] = useState<LinkType>(banner?.linkType ?? 'none');

    useEffect(() => {
        if (banner && (isEdit || isView)) {
            reset({
                name: banner.name,
                imageId: banner.imageId,
                linkType: banner.linkType,
                linkTargetId: banner.linkTargetId,
            });
            setCurrentLinkType(banner.linkType);
        } else {
            reset({ name: '', imageId: null, linkType: 'none', linkTargetId: null });
            setCurrentLinkType('none');
        }
        setIsTargetListOpen(!(banner && (isEdit || isView) && banner.linkTargetId));
    }, [banner, isEdit, isView, reset]);

    useEffect(() => {
        if (!isOpen) {
            reset({ name: '', imageId: null, linkType: 'none', linkTargetId: null });
            setCurrentLinkType('none');
        }
    }, [isOpen, reset]);

    const linkTargetId = watch('linkTargetId');

    const [targetSearch, setTargetSearch] = useState('');

    const kioskProducts = useMemo(() => products.filter((p) => p.kioskAvailability !== false), [products]);

    const sortedProducts = useMemo(() => {
        if (!linkTargetId) return kioskProducts;
        return [...kioskProducts].sort((a, b) => {
            if (a.productId === linkTargetId) return -1;
            if (b.productId === linkTargetId) return 1;
            return 0;
        });
    }, [kioskProducts, linkTargetId]);

    const sortedCategories = useMemo(() => {
        if (!linkTargetId) return categories;
        return [...categories].sort((a, b) => {
            if (a.categoryId === linkTargetId) return -1;
            if (b.categoryId === linkTargetId) return 1;
            return 0;
        });
    }, [categories, linkTargetId]);

    const filteredProducts = useMemo(() => {
        const search = targetSearch.toLowerCase();
        if (!search) return sortedProducts;
        return sortedProducts.filter(
            (p) => p.name.toLowerCase().includes(search) || p.sku?.value?.toLowerCase().includes(search),
        );
    }, [sortedProducts, targetSearch]);

    const filteredCategories = useMemo(() => {
        const search = targetSearch.toLowerCase();
        if (!search) return sortedCategories;
        return sortedCategories.filter((c) => c.name.toLowerCase().includes(search));
    }, [sortedCategories, targetSearch]);

    const selectedProductName = useMemo(() => {
        if (currentLinkType !== 'product' || !linkTargetId) return null;
        return products.find((p) => p.productId === linkTargetId)?.name ?? null;
    }, [products, currentLinkType, linkTargetId]);

    const selectedCategoryName = useMemo(() => {
        if (currentLinkType !== 'category' || !linkTargetId) return null;
        return categories.find((c) => c.categoryId === linkTargetId)?.name ?? null;
    }, [categories, currentLinkType, linkTargetId]);

    const withServiceHoursCheck = useCallback(
        (action: () => void) => {
            if (isInServiceHours(schedules)) {
                setPendingAction(() => action);
                setShowConfirmReload(true);
            } else {
                action();
            }
        },
        [schedules],
    );

    const handleConfirmReload = useCallback(() => {
        if (pendingAction) {
            pendingAction();
        }
        setShowConfirmReload(false);
        setPendingAction(null);
    }, [pendingAction]);

    const handleCancelReload = useCallback(() => {
        setShowConfirmReload(false);
        setPendingAction(null);
    }, []);

    const submitCreate = useCallback(
        async (data: FormValues, isActive: boolean) => {
            if (!data.imageId) return;
            await createBanner({
                token,
                restaurantId,
                name: data.name,
                imageId: data.imageId,
                isActive,
                linkType: data.linkType,
                linkTargetId: data.linkType === 'none' ? null : data.linkTargetId,
                channelId: ChannelId.KIOSK,
            });
            onHide();
        },
        [token, restaurantId, createBanner, onHide],
    );

    const submitUpdate = useCallback(
        async (data: FormValues) => {
            if (!banner) return;
            await updateBanner({
                token,
                promotionalBannerId: banner.promotionalBannerId,
                name: data.name,
                imageId: data.imageId ?? undefined,
                linkType: data.linkType,
                linkTargetId: data.linkType === 'none' ? null : data.linkTargetId,
            });
            onHide();
        },
        [token, banner, updateBanner, onHide],
    );

    const onSubmitActive = handleSubmit((data) => {
        const doSubmit = () => {
            if (isEdit) {
                submitUpdate(data);
            } else {
                submitCreate(data, true);
            }
        };
        if (isActiveBanner || !isEdit) {
            withServiceHoursCheck(doSubmit);
        } else {
            doSubmit();
        }
    });

    const onSubmitDraft = handleSubmit((data) => {
        submitCreate(data, false);
    });

    const onDeactivate = useCallback(() => {
        if (!banner) return;
        const doDeactivate = async () => {
            await toggleBanner({
                token,
                promotionalBannerId: banner.promotionalBannerId,
                isActive: false,
            });
            onHide();
        };
        withServiceHoursCheck(doDeactivate);
    }, [token, banner, toggleBanner, onHide, withServiceHoursCheck]);

    const buttons = useMemo(() => {
        if (isActiveBanner) {
            return (
                <>
                    <Button buttonType="outlinedPrimary" onClick={onDeactivate}>
                        <FormattedMessage id="promotionalBanner.modal.deactivateBanner" />
                    </Button>
                    <Button buttonType="primary" onClick={onSubmitActive} disabled={!isDirty}>
                        <FormattedMessage id="promotionalBanner.modal.updateBanner" />
                    </Button>
                </>
            );
        }
        if (isEdit) {
            return (
                <Button buttonType="primary" onClick={onSubmitActive} disabled={!isDirty}>
                    <FormattedMessage id="promotionalBanner.modal.updateBanner" />
                </Button>
            );
        }
        return (
            <>
                <Button buttonType="outlinedPrimary" onClick={onSubmitDraft}>
                    <FormattedMessage id="promotionalBanner.modal.saveAsDraft" />
                </Button>
                <Button buttonType="primary" onClick={onSubmitActive} disabled={!isValid}>
                    <FormattedMessage id="promotionalBanner.modal.activateBanner" />
                </Button>
            </>
        );
    }, [isActiveBanner, isEdit, isDirty, isValid, onSubmitActive, onSubmitDraft, onDeactivate]);

    const linkTypeOptions: { value: LinkType; labelKey: string; descKey: string }[] = [
        {
            value: 'none',
            labelKey: 'promotionalBanner.modal.linkNone',
            descKey: 'promotionalBanner.modal.linkNoneDesc',
        },
        {
            value: 'product',
            labelKey: 'promotionalBanner.modal.linkProduct',
            descKey: 'promotionalBanner.modal.linkProductDesc',
        },
        {
            value: 'category',
            labelKey: 'promotionalBanner.modal.linkCategory',
            descKey: 'promotionalBanner.modal.linkCategoryDesc',
        },
    ];

    const handleImageChange = useCallback(
        (newImageId: number | null) => {
            setValue('imageId', newImageId, { shouldDirty: true });
        },
        [setValue],
    );

    return (
        <>
            <ConfirmReloadModal
                isOpen={showConfirmReload}
                onConfirm={handleConfirmReload}
                onCancel={handleCancelReload}
            />
            <Modal
                isOpen={isOpen}
                onHide={onHide}
                title={(() => {
                    if (isView) return 'promotionalBanner.modal.viewTitle';
                    if (isEdit) return 'promotionalBanner.modal.editTitle';
                    return 'promotionalBanner.modal.createTitle';
                })()}
                buttons={isView ? undefined : buttons}
                width={720}
            >
                <div style={isView ? { pointerEvents: 'none', opacity: 0.8 } : undefined}>
                    {/* Name */}
                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: !isView }}
                        render={({ field }) => (
                            <div>
                                <Input
                                    {...field}
                                    labelId="promotionalBanner.modal.nameLabel"
                                    required={!isView}
                                    disabled={isView}
                                />
                                <HelperText>
                                    <FormattedMessage id="promotionalBanner.modal.nameHelper" />
                                </HelperText>
                            </div>
                        )}
                    />

                    {/* Channel */}
                    <SectionLabel required>
                        <FormattedMessage id="promotionalBanner.modal.channelLabel" />
                    </SectionLabel>
                    <RadioCardGroup>
                        <RadioCard selected>
                            <ChannelCheck>&#10003;</ChannelCheck>
                            <RadioCardContent>
                                <RadioCardTitle selected>
                                    {computeText(intl, 'promotionalBanner.modal.channelKiosk')}
                                </RadioCardTitle>
                                <RadioCardDescription>
                                    {computeText(intl, 'promotionalBanner.modal.channelKioskDesc')}
                                </RadioCardDescription>
                            </RadioCardContent>
                        </RadioCard>
                        <RadioCard disabled>
                            <RadioCardContent>
                                <RadioCardTitle>
                                    {computeText(intl, 'promotionalBanner.modal.channelWeb')}
                                </RadioCardTitle>
                                <RadioCardDescription>
                                    {computeText(intl, 'promotionalBanner.modal.channelWebDesc')}
                                </RadioCardDescription>
                                <DisabledInfo>
                                    <SVGIcon icon={SVGIcons.INFO} size={14} />
                                    {computeText(intl, 'promotionalBanner.modal.channelWebDisabled')}
                                </DisabledInfo>
                            </RadioCardContent>
                        </RadioCard>
                    </RadioCardGroup>

                    {/* Image */}
                    <SectionLabel required>
                        <FormattedMessage id="promotionalBanner.modal.imageLabel" />
                    </SectionLabel>
                    <Controller
                        name="imageId"
                        control={control}
                        rules={{ required: !isView }}
                        render={({ field }) => (
                            <BannerImageSection
                                restaurantId={restaurantId}
                                imageId={field.value}
                                defaultImage={
                                    banner?.image
                                        ? { fullUrl: banner.image.fullUrl, imageId: banner.image.imageId }
                                        : undefined
                                }
                                imageSpecsMessageId="promotionalBanner.modal.imageSpecs"
                                imageDescriptionMessageId="promotionalBanner.modal.imageFileDescription"
                                replaceMessageId="promotionalBanner.modal.replace"
                                errorRatioMessageId="promotionalBanner.modal.image.error.ratio"
                                onChange={(newImageId) => {
                                    handleImageChange(newImageId);
                                    field.onChange(newImageId);
                                }}
                            />
                        )}
                    />

                    {/* Link type */}
                    <SectionLabel required>
                        <FormattedMessage id="promotionalBanner.modal.linkTypeLabel" />
                    </SectionLabel>
                    <Controller
                        name="linkType"
                        control={control}
                        render={({ field }) => (
                            <RadioCardGroup>
                                {linkTypeOptions.map((option) => (
                                    <RadioCard
                                        key={option.value}
                                        selected={field.value === option.value}
                                        onClick={() => {
                                            field.onChange(option.value);
                                            setCurrentLinkType(option.value);
                                            if (option.value === 'none') {
                                                setValue('linkTargetId', null);
                                            } else {
                                                setTargetSearch('');
                                                setIsTargetListOpen(true);
                                            }
                                        }}
                                    >
                                        <RadioDot selected={field.value === option.value} />
                                        <RadioCardContent>
                                            <RadioCardTitle>{computeText(intl, option.labelKey)}</RadioCardTitle>
                                            <RadioCardDescription>
                                                {computeText(intl, option.descKey)}
                                            </RadioCardDescription>
                                        </RadioCardContent>
                                    </RadioCard>
                                ))}
                            </RadioCardGroup>
                        )}
                    />

                    {currentLinkType === 'category' && (
                        <Controller
                            name="linkTargetId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <>
                                    <SectionLabel required>
                                        <FormattedMessage id="promotionalBanner.modal.linkCategory" />
                                    </SectionLabel>
                                    {field.value && !isTargetListOpen && (
                                        <SelectedItem onClick={() => setIsTargetListOpen(true)}>
                                            <ProductName>{selectedCategoryName}</ProductName>
                                            <SvyIcon icon="pencil-line" />
                                        </SelectedItem>
                                    )}
                                    {(isTargetListOpen || !field.value) && (
                                        <>
                                            <SearchInput
                                                placeholder={computeText(
                                                    intl,
                                                    'promotionalBanner.modal.searchCategory',
                                                )}
                                                value={targetSearch}
                                                onChange={(e) => setTargetSearch(e.target.value)}
                                            />
                                            <ProductList>
                                                {filteredCategories.map((cat) => (
                                                    <ProductItem
                                                        key={cat.categoryId}
                                                        selected={field.value === cat.categoryId}
                                                        onClick={() => {
                                                            field.onChange(cat.categoryId);
                                                            setIsTargetListOpen(false);
                                                            setTargetSearch('');
                                                        }}
                                                    >
                                                        <ProductName>{cat.name}</ProductName>
                                                    </ProductItem>
                                                ))}
                                            </ProductList>
                                        </>
                                    )}
                                </>
                            )}
                        />
                    )}

                    {currentLinkType === 'product' && (
                        <Controller
                            name="linkTargetId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <>
                                    <SectionLabel required>
                                        <FormattedMessage id="promotionalBanner.modal.linkProduct" />
                                    </SectionLabel>
                                    {field.value && !isTargetListOpen && (
                                        <SelectedItem onClick={() => setIsTargetListOpen(true)}>
                                            <ProductName>{selectedProductName}</ProductName>
                                            <SvyIcon icon="pencil-line" />
                                        </SelectedItem>
                                    )}
                                    {(isTargetListOpen || !field.value) && (
                                        <>
                                            <SearchInput
                                                placeholder={computeText(intl, 'promotionalBanner.modal.searchProduct')}
                                                value={targetSearch}
                                                onChange={(e) => setTargetSearch(e.target.value)}
                                            />
                                            <ProductList>
                                                {filteredProducts.map((product) => (
                                                    <ProductItem
                                                        key={product.productId}
                                                        selected={field.value === product.productId}
                                                        onClick={() => {
                                                            field.onChange(product.productId);
                                                            setIsTargetListOpen(false);
                                                            setTargetSearch('');
                                                        }}
                                                    >
                                                        {product.image?.fullUrl ? (
                                                            <ProductThumb
                                                                src={product.image.fullUrl}
                                                                alt={product.name}
                                                            />
                                                        ) : (
                                                            <ProductThumbPlaceholder />
                                                        )}
                                                        <ProductName>{product.name}</ProductName>
                                                        {product.categories[0] && (
                                                            <ProductMeta>
                                                                <ProductMetaBold>
                                                                    {computeText(
                                                                        intl,
                                                                        'promotionalBanner.modal.linkCategory',
                                                                    )}{' '}
                                                                    :
                                                                </ProductMetaBold>
                                                                {product.categories[0].name}
                                                            </ProductMeta>
                                                        )}
                                                        {product.sku && (
                                                            <ProductMeta>
                                                                <ProductMetaBold>SKU :</ProductMetaBold>
                                                                {product.sku.value}
                                                            </ProductMeta>
                                                        )}
                                                    </ProductItem>
                                                ))}
                                            </ProductList>
                                        </>
                                    )}
                                </>
                            )}
                        />
                    )}
                </div>
            </Modal>
        </>
    );
};
